import React, { useState } from "react";
import {
  makeStyles,
  Box,
  Chip,
  TextField,
  Typography,
  IconButton,
  Collapse,
  Button,
} from "@material-ui/core";
import TuneIcon from "@material-ui/icons/Tune";
import AddIcon from "@material-ui/icons/Add";
import { getMatchTarget, setMatchTarget } from "../../../utils/matchScore";

const useStyles = makeStyles((theme) => ({
  root: {
    background: "#ffffff",
    padding: theme.spacing(1.5),
    margin: `${theme.spacing(1)}px 0`,
    borderRadius: 4,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  title: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontWeight: 600,
  },
  body: {
    marginTop: theme.spacing(1),
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  hint: {
    color: theme.palette.grey[600],
    fontSize: "0.8rem",
    marginTop: theme.spacing(0.5),
  },
}));

const MatchTargetEditor = ({ onChange }) => {
  const classes = useStyles();
  const initial = getMatchTarget();
  const [open, setOpen] = useState(initial.skills.length > 0);
  const [skills, setSkills] = useState(initial.skills);
  const [minExperience, setMinExperience] = useState(initial.minExperience);
  const [input, setInput] = useState("");

  const persist = (next) => {
    setMatchTarget(next);
    if (onChange) onChange(next);
  };

  const addSkill = () => {
    const v = input.trim();
    if (!v) return;
    if (skills.some((s) => s.toLowerCase() === v.toLowerCase())) {
      setInput("");
      return;
    }
    const next = [...skills, v];
    setSkills(next);
    setInput("");
    persist({ skills: next, minExperience });
  };

  const removeSkill = (s) => {
    const next = skills.filter((x) => x !== s);
    setSkills(next);
    persist({ skills: next, minExperience });
  };

  const updateExperience = (val) => {
    const num = Math.max(0, Number(val) || 0);
    setMinExperience(num);
    persist({ skills, minExperience: num });
  };

  const clearAll = () => {
    setSkills([]);
    setMinExperience(0);
    persist({ skills: [], minExperience: 0 });
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="subtitle2" className={classes.title}>
          <TuneIcon fontSize="small" /> AI Match Settings
          {skills.length > 0 && (
            <Chip
              size="small"
              label={`${skills.length} target skill${skills.length > 1 ? "s" : ""}`}
              color="primary"
              style={{ marginLeft: 8 }}
            />
          )}
        </Typography>
        <Button size="small" onClick={() => setOpen((o) => !o)}>
          {open ? "Hide" : "Configure"}
        </Button>
      </Box>

      <Collapse in={open}>
        <Box className={classes.body}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Define the role you're hiring for. Each candidate gets an AI Match
            score based on skills overlap and experience fit.
          </Typography>

          <Box className={classes.row}>
            <TextField
              size="small"
              label="Add target skill"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addSkill();
                }
              }}
              placeholder="e.g. React"
            />
            <IconButton color="primary" onClick={addSkill} size="small">
              <AddIcon />
            </IconButton>

            <TextField
              size="small"
              type="number"
              label="Min. years exp."
              value={minExperience}
              onChange={(e) => updateExperience(e.target.value)}
              inputProps={{ min: 0, max: 40 }}
              style={{ width: 120 }}
            />

            {(skills.length > 0 || minExperience > 0) && (
              <Button size="small" onClick={clearAll}>
                Clear
              </Button>
            )}
          </Box>

          {skills.length > 0 && (
            <Box className={classes.chips}>
              {skills.map((s) => (
                <Chip
                  key={s}
                  label={s}
                  onDelete={() => removeSkill(s)}
                  color="primary"
                />
              ))}
            </Box>
          )}

          <Typography className={classes.hint}>
            Settings are saved to this browser. Score = 80% skill overlap + 20%
            experience fit.
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
};

export default MatchTargetEditor;
